// fileUploadActions.js
import apiClient from "@/services/api/apiClient";
import { createAction } from "@reduxjs/toolkit";
import { closeModal } from "@store/slices/modalSlice";
import { fetchFoldersInfo } from "@store/thunks/dashboardThunk";
import { BASE_URL, DEVNAGRI_ID, GAADMIN_ID, ROLE_ROUTES } from "@utils/constants";

// Action types
export const FILE_UPLOAD_START = "FILE_UPLOAD_START";
export const FILE_UPLOAD_SUCCESS = "FILE_UPLOAD_SUCCESS";
export const FILE_UPLOAD_FAILURE = "FILE_UPLOAD_FAILURE";
export const FILE_UPLOAD_PROGRESS = "FILE_UPLOAD_PROGRESS";

// Action creators
export const fileUploadStart = createAction(FILE_UPLOAD_START);
export const fileUploadSuccess = createAction(FILE_UPLOAD_SUCCESS);
export const fileUploadFailure = createAction(FILE_UPLOAD_FAILURE);
export const fileUploadProgress = createAction(FILE_UPLOAD_PROGRESS);

// Fetch languages for dropdown
export const fetchLanguages = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const { token } = state.auth;
    
    const data = await apiClient(`${BASE_URL}api/user/languages`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
    });
    console.log("Languages fetched:", data);
    return data.data || [];
  } catch (error) {
    console.error("Error fetching languages:", error);
    return [];
  }
};

// Thunk for file upload
export const uploadFiles = (activeTab, lang, files, folderId, deadline) => async (dispatch, getState) => {
  // Validate input
  if (!files || files.length === 0) {
    dispatch(fileUploadFailure("No files selected"));
    return { success: false, error: "No files selected" };
  }

  // Ensure folderId is valid
  if (!folderId) {
    dispatch(fileUploadFailure("No folder selected. Please select a folder first."));
    return { success: false, error: "No folder selected" };
  }

  // Dispatch upload start action
  dispatch(fileUploadStart());

  try {
    const state = getState();
    const userId = state.auth.userId;

    // Track successful uploads
    let successfulUploads = 0;
    const totalFiles = files.length;

    for (const file of files) {
      console.log("file:", file, "is instance of File?", file instanceof File);
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("uploadedBy", userId);
      formData.append("uploadedFor", activeTab === 'GA' ? GAADMIN_ID : DEVNAGRI_ID);
      formData.append("sourceLanguageId", 2);
      formData.append("folderId", folderId.toString()); // Ensure folderId is a string
      
      // Add target language for Devnagri uploads
      if (activeTab !== 'GA' && lang) {
        formData.append("targetLanguageId", lang);
      }
      
      // Add deadline if provided
      if (deadline) {
        formData.append("deadline", deadline);
      }

      // Log form data for debugging
      console.log("Uploading file with form data:");
      console.log(formData)
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
      }

      const url = activeTab === 'GA' 
        ? `${BASE_URL}api/superAdmin/file/uploadGS` 
        : `${BASE_URL}api/superAdmin/file/uploadDev`;

      const uploadedFile = await apiClient(url, {
        method: "POST",
        credentials: "include",
        headers:{
          "Authorization": `Bearer ${state.auth.token}`
        },
        body: formData,
      });
      console.log("Upload successful:", uploadedFile);
      
      // Count successful uploads
      successfulUploads++;
    }

    // If all uploads were successful
    if (successfulUploads === totalFiles) {
      // Dispatch success action
      dispatch(fileUploadSuccess({ 
        message: `Successfully uploaded ${successfulUploads} files`,
        folderId: folderId
      }));
      
      // Refresh the file list without changing location
      dispatch(fetchFoldersInfo(activeTab, folderId));
      
      // Close the modal after a short delay
      setTimeout(() => {
        dispatch(closeModal("fileUpload"));
      }, 500);
    }
    
    return { 
      success: true, 
      message: `Successfully uploaded ${successfulUploads} of ${totalFiles} files`,
      folderId: folderId
    };
  } catch (error) {
    console.error("Upload error:", error);
    
    // Dispatch failure action with error message
    dispatch(fileUploadFailure(error.message || "Upload failed"));
    
    // Return error for potential further handling
    return { success: false, error: error.message || "Upload failed" };
  }
};
